import { Fragment } from 'react';
import { BREAKPOINTS_WIDTHS, formatNumber, useWindowSize } from '@belong/common';
import { IconInfo } from '@belong/icons';
import { Text, Tooltip } from '@belong/ui';
import clsx from 'clsx';
import { PayoutPlanVariationModel } from 'src/api/models';
import { usePayoutPlans, useRegions } from 'src/features/app';
import { From } from 'src/modals/region-select/region-select';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import css from './price-table.module.css';
import { STRINGS } from './price-table.strings';
import { TablePayoutPlans } from './table-payout-plans';

/* eslint-disable tailwindcss/no-arbitrary-value */
const getHomeownerPriceTableConfig = ({
  regionId,
  from,
  payoutPlan,
  isMobile,
}: {
  regionId?: number;
  from: From;
  payoutPlan?: Record<string, PayoutPlanVariationModel>;
  isMobile: boolean;
}) => {
  const hasVariableFees = payoutPlan?.Monthly.defaultPayoutPlanType === 'Variable';
  const isOnNationallyRegion = regionId === 0;

  return {
    header: [
      <>
        <Text variant="body" fontWeight="semibold">
          {'Belong'}
        </Text>
      </>,
      <>
        <Text variant="body" fontWeight="semibold">
          {'Property Managers'}
        </Text>
      </>,
    ],
    items: [
      ...(hasVariableFees
        ? [
            {
              name: (
                <div className="flex gap-xs justify-between md:justify-start">
                  <span className="whitespace-pre-line">{STRINGS.variableFees.item1.name}</span>
                  <Tooltip
                    content={
                      <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                        <Text fontWeight="semibold" className="text-white">
                          {STRINGS.variableFees.item1.text}
                        </Text>
                      </div>
                    }
                    contentProps={
                      isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
                    }
                    arrowProps={{
                      className: 'fill-dark-navy',
                    }}
                  >
                    <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                      <IconInfo width={20} />
                    </div>
                  </Tooltip>
                </div>
              ),
              options: STRINGS.variableFees.item1.options,
            },
            {
              name: (
                <div className="flex gap-xs justify-between md:justify-start">
                  <span className="whitespace-pre-line">{STRINGS.variableFees.item2.name}</span>
                  <Tooltip
                    content={
                      <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                        <Text fontWeight="semibold" className="text-white">
                          {STRINGS.variableFees.item2.text}
                        </Text>
                      </div>
                    }
                    contentProps={
                      isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
                    }
                    arrowProps={{
                      className: 'fill-dark-navy',
                    }}
                  >
                    <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                      <IconInfo width={20} />
                    </div>
                  </Tooltip>
                </div>
              ),
              options: STRINGS.variableFees.item2.options,
            },
          ]
        : [
            {
              name: (
                <div className="flex gap-xs justify-between md:justify-start">
                  <span className="whitespace-pre-line w-[calc(100%_-_20px)] md:w-[164px] lg:w-auto">
                    {STRINGS.fixedFees.item1.name}
                  </span>
                  <Tooltip
                    content={
                      <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                        <Text fontWeight="semibold" className="text-white">
                          {STRINGS.fixedFees.item1.text}
                        </Text>
                      </div>
                    }
                    contentProps={
                      isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
                    }
                    arrowProps={{
                      className: 'fill-dark-navy',
                    }}
                  >
                    <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                      <IconInfo width={20} />
                    </div>
                  </Tooltip>
                </div>
              ),
              options: isOnNationallyRegion
                ? STRINGS.fixedFees.item1Nationally.options
                : [
                    <>
                      <div className="w-auto sm:w-[150px] md:w-auto">
                        <div className="contents md:hidden">
                          <Text fontWeight="semibold" variant="p1">
                            {STRINGS.fixedFees.item1.options[0]}
                          </Text>
                        </div>
                        <div className="hidden md:contents lg:block lg:w-5xl xl:w-auto">
                          <Text fontWeight="semibold">{STRINGS.fixedFees.item1.options[0]}</Text>
                        </div>
                      </div>
                    </>,
                    STRINGS.fixedFees.item1.options[1],
                  ],
            },
          ]),
      {
        name: (
          <div className="flex gap-xs justify-between md:justify-start">
            <span className="whitespace-pre-line">{STRINGS.item1.name}</span>
            <Tooltip
              content={
                <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                  <Text fontWeight="semibold" className="text-white">
                    {STRINGS.item1.text}
                  </Text>
                </div>
              }
              contentProps={
                isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
              }
              arrowProps={{
                className: 'fill-dark-navy',
              }}
            >
              <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                <IconInfo width={20} />
              </div>
            </Tooltip>
          </div>
        ),
        options: STRINGS.item1.options,
      },
      {
        name: (
          <div className="flex gap-xs justify-between md:justify-start">
            <span className="whitespace-pre-line md:w-[157px] lg:w-auto">{STRINGS.item2.name}</span>
            <Tooltip
              content={
                <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                  <Text fontWeight="semibold" className="text-white">
                    {STRINGS.item2.text}
                  </Text>
                </div>
              }
              contentProps={
                isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
              }
              arrowProps={{
                className: 'fill-dark-navy',
              }}
            >
              <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                <IconInfo width={20} />
              </div>
            </Tooltip>
          </div>
        ),
        options: STRINGS.item2.options,
      },
      {
        name: (
          <div className="flex gap-xs justify-between md:justify-start">
            <span className="whitespace-pre-line">{STRINGS.item3.name}</span>
            <Tooltip
              content={
                <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                  <Text fontWeight="semibold" className="text-white">
                    {STRINGS.item3.text}
                  </Text>
                </div>
              }
              contentProps={
                isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
              }
              arrowProps={{
                className: 'fill-dark-navy',
              }}
            >
              <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                <IconInfo width={20} />
              </div>
            </Tooltip>
          </div>
        ),
        options: STRINGS.item3.options,
      },
      {
        name: (
          <div className="flex gap-xs justify-between md:justify-start">
            <span className="whitespace-pre-line">{STRINGS.item4.name}</span>
            <Tooltip
              content={
                <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                  <Text fontWeight="semibold" className="text-white">
                    {STRINGS.item4.text}
                  </Text>
                </div>
              }
              contentProps={
                isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
              }
              arrowProps={{
                className: 'fill-dark-navy',
              }}
            >
              <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                <IconInfo width={20} />
              </div>
            </Tooltip>
          </div>
        ),
        options: STRINGS.item4.options,
      },
      {
        name: (
          <div className="flex gap-xs justify-between md:justify-start">
            <span className="whitespace-pre-line">{STRINGS.item5.name}</span>
            <Tooltip
              content={
                <div className="p-2sm rounded bg-dark-navy max-w-xs md:max-w-md outline-none">
                  <Text fontWeight="semibold" className="text-white">
                    {STRINGS.item5.text}
                  </Text>
                </div>
              }
              contentProps={
                isMobile ? { side: 'top', align: 'start', alignOffset: -100, className: 'outline-none' } : {}
              }
              arrowProps={{
                className: 'fill-dark-navy',
              }}
            >
              <div className="flex flex-row justify-center gap-2xs cursor-pointer shrink-0">
                <IconInfo width={20} />
              </div>
            </Tooltip>
          </div>
        ),
        options: STRINGS.item5.options,
      },
      ...(hasVariableFees
        ? [
            {
              name: STRINGS.variableFees.item3.name,
              options: [
                <TablePayoutPlans
                  keyName="managementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumManagementFeeAmount)} Minimum`}
                />,
                STRINGS.variableFees.item3.options[1],
              ],
            },
            {
              name: STRINGS.variableFees.item4.name,
              options: [
                <TablePayoutPlans
                  keyName="placementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumPlacementFeeAmount)} Minimum`}
                />,
                STRINGS.variableFees.item4.options[1],
              ],
            },
            {
              name: STRINGS.fixedFees.item4.name,
              options: [
                <TablePayoutPlans
                  keyName="renewalFeePercent"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                  showTooltip
                  tooltipText={`$${formatNumber(payoutPlan?.Monthly?.minimumRenewalFeeAmount)} Minimum`}
                />,
                STRINGS.fixedFees.item4.options[1],
              ],
            },
          ]
        : [
            {
              name: STRINGS.fixedFees.item2.name,
              options: [
                <TablePayoutPlans
                  keyName="managementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                />,
                STRINGS.fixedFees.item2.options[1],
              ],
            },
            {
              name: STRINGS.fixedFees.item3.name,
              options: [
                <TablePayoutPlans
                  keyName="placementFeePercentage"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                />,
                STRINGS.fixedFees.item3.options[1],
              ],
            },
            {
              name: STRINGS.fixedFees.item4.name,
              options: [
                <TablePayoutPlans
                  keyName="renewalFeePercent"
                  regionId={regionId}
                  key={1}
                  from={from}
                  payoutPlan={payoutPlan}
                />,
                STRINGS.fixedFees.item4.options[1],
              ],
            },
          ]),
      {
        name: STRINGS.item6.name,
        options: STRINGS.item6.options,
      },
      {
        name: STRINGS.item7.name,
        options: STRINGS.item7.options,
      },
      {
        name: STRINGS.item8.name,
        options: STRINGS.item8.options,
      },
      {
        name: STRINGS.item9.name,
        options: STRINGS.item9.options,
      },
      {
        name: STRINGS.item10.name,
        options: STRINGS.item10.options,
      },
      {
        name: STRINGS.item11.name,
        options: STRINGS.item11.options,
      },
      {
        name: STRINGS.item12.name,
        options: STRINGS.item12.options,
      },
      {
        name: STRINGS.item13.name,
        options: STRINGS.item13.options,
      },
      {
        name: STRINGS.item14.name,
        options: STRINGS.item14.options,
      },
      {
        name: STRINGS.item15.name,
        options: STRINGS.item15.options,
      },
      {
        name: STRINGS.item16.name,
        options: [
          STRINGS.item16.options[0],
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item16.options[1].split('/')[0]}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item16.options[1].split('/')[1]}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {STRINGS.item16.options[1]}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{STRINGS.item16.options[1]}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: STRINGS.item17.name,
        options: [
          STRINGS.item17.options[0],
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item17.options[1].split('/')[0]}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item17.options[1].split('/')[1]}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {STRINGS.item17.options[1]}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{STRINGS.item17.options[1]}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: STRINGS.item18.name,
        options: [
          STRINGS.item18.options[0],
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item18.options[1].split('/')[0]}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item18.options[1].split('/')[1]}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {STRINGS.item18.options[1]}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{STRINGS.item18.options[1]}</Text>
              </div>
            </div>
          </>,
        ],
      },
      {
        name: STRINGS.item19.name,
        options: [
          STRINGS.item19.options[0],
          <>
            <div className="px-2xs min-[420px]:hidden">
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item19.options[1].split('/')[0]}/
              </Text>
              <Text fontWeight="semibold" variant="p1">
                {STRINGS.item19.options[1].split('/')[1]}
              </Text>
            </div>
            <div className="hidden min-[420px]:block">
              <div className="contents md:hidden">
                <Text fontWeight="semibold" variant="p1">
                  {STRINGS.item19.options[1]}
                </Text>
              </div>
              <div className="hidden md:contents">
                <Text fontWeight="semibold">{STRINGS.item19.options[1]}</Text>
              </div>
            </div>
          </>,
        ],
      },
    ],
  };
};
/* eslint-enable tailwindcss/no-arbitrary-value */

const getCellClassName = (rowIdx: number): string =>
  clsx('border border-solid border-gray border-t-0 border-x-0', { 'bg-light-gray': rowIdx % 2 !== 0 });

const getFirstColClassName = (rowIdx: number): string =>
  clsx(getCellClassName(rowIdx), 'col-span-3 flex flex-col justify-center px-xs py-2sm sm:py-xs min-h-header');

type PriceTableProps = {
  regionId?: number;
  from?: From;
};

export function PriceTable({ regionId, from = 'homeowners' }: PriceTableProps): JSX.Element {
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINTS_WIDTHS.LG;
  const { regions } = useRegions();
  const selectedRegion = useStore(regionSelector);
  const { payoutPlans } = usePayoutPlans();

  const propRegion = regions.find((region) => region.id === regionId);
  const { id } = propRegion || selectedRegion;
  const payoutPlan = payoutPlans?.[id || 0]?.DEFAULT;
  const config = getHomeownerPriceTableConfig({ regionId, from, payoutPlan, isMobile });

  return (
    <div className="w-full relative">
      <div className="border border-solid border-gray w-full grid grid-cols-7 auto-rows-min my-2xs border-b-0">
        <div className={getFirstColClassName(-1)} />
        {config.header.map((header, idx) => (
          <div
            className={clsx(
              getCellClassName(-1),
              'col-span-2 flex justify-center items-center text-center px-2xs',
              idx === 0 && css.belongRow
            )}
            key={idx}
          >
            <div
              className={clsx(
                idx === 0 &&
                  'border-x-4 border-x-navy border-b-0 border-t-4 border-t-navy border-solid flex items-center justify-center rounded-small',
                idx === 0 && css.belongTrailingCell
              )}
            >
              {header}
            </div>
          </div>
        ))}
        {config.items.map((item, idx) => (
          <Fragment key={idx}>
            <div className={getFirstColClassName(idx)}>
              <Text as="div" className="md:hidden" variant="p1" fontWeight="semibold">
                {item.name}
              </Text>
              <Text as="div" className="hidden md:block" variant="body" fontWeight="semibold">
                {item.name}
              </Text>
            </div>
            {item.options.map((option: string | JSX.Element, optionIdx: number) => {
              return (
                <div
                  className={clsx(
                    getCellClassName(idx),
                    'col-span-2 flex justify-center items-center text-center px-2xs',
                    optionIdx === 0 && css.belongRow
                  )}
                  key={optionIdx}
                >
                  <div
                    className={clsx(
                      'flex items-center justify-center px-2xs',
                      optionIdx === 0 && 'border-x-4 border-x-navy border-y-0 border-solid',
                      optionIdx === 0 && css.belongItemCell,
                      idx === config.items.length - 1 && 'border-b-4 border-b-navy rounded-b-small',
                      idx === config.items.length - 1 && css.belongTrailingCell
                    )}
                  >
                    {typeof option === 'string' ? (
                      <>
                        <Text fontWeight="semibold" variant="p1" className="md:hidden">
                          {option}
                        </Text>
                        <Text fontWeight="semibold" variant="body" className="hidden md:block">
                          {option}
                        </Text>
                      </>
                    ) : (
                      option
                    )}
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
